import HiddenInput from "@/components/form/components/HiddenInput";
import {
  datePickerClearableProps,
  datePickerFormItemProps,
  rangePickerFormItemProps,
  selectStandardProps,
} from "@/utils/componentUtils";
import { getDatePickerDefaultRanges, getDatePickerFormat } from "@/utils/formUtils";
import { dateToIsoDateString } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { Checkbox, DatePicker, Form, Select } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { useIsCurrentAgentTypePlus } from "../../../auth/hooks";
import { CommissionsSettingsStatus } from "../../../commissions/settings/enums";
import { AgentEducationTreeSelect } from "../../../enumerations/components/form/AgentEducationTreeSelect";
import CommissionsLevelSelect from "../../../enumerations/components/form/CommissionsLevelSelect";
import { CommissionsSettingsRankSelect } from "../../../enumerations/components/form/CommissionsSettingsRankSelect";
import { AgentEducationAcademyType, AgentSecondaryStatus, AgentStatus } from "../../enums";
import type { CommonAgentFilterRequest } from "../../types";

type FilterFields = Pick<
  CommonAgentFilterRequest,
  | "completedEducationIds"
  | "completedEducationDateMin"
  | "completedEducationDateMax"
  | "completedAcademies"
  | "completedAcademyDateMin"
  | "completedAcademyDateMax"
> & {
  completedEducationDateRange?: [Dayjs, Dayjs];
  completedAcademyDateRange?: [Dayjs, Dayjs];
};

type Props = {
  hidden?: boolean;
  includeZeroBalance?: boolean;
};

export const AgentExtendedFilterFormFields = ({ hidden, includeZeroBalance }: Props) => {
  const { t } = useTranslation();

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const form = Form.useFormInstance<FilterFields>();
  const completedEducationIds = Form.useWatch("completedEducationIds", form);
  const completedAcademies = Form.useWatch("completedAcademies", form);

  return (
    <>
      <CommissionsLevelSelect
        formItemProps={{
          name: "commissionsSettingsLevelIds",
          label: t("agent.filter.commissionsSettingsLevelIds"),
          rules: [validations.none],
          hidden,
        }}
        selectProps={{
          allowClear: true,
          mode: "multiple",
          maxTagCount: "responsive",
        }}
        optionsProps={{ renderOnlyLevelCodeTag: true }}
      />

      <Form.Item
        name="commissionsSettingsRankIds"
        label={t("agent.filter.commissionsSettingsRankIds")}
        rules={[validations.none]}
        hidden={hidden}
      >
        <CommissionsSettingsRankSelect isMultiple allowClear />
      </Form.Item>

      <Form.Item
        name="agentCreatedAtDateMin"
        label={t("agent.filter.agentCreatedAtDateMin")}
        rules={[validations.notFuture]}
        {...datePickerFormItemProps}
        hidden={hidden}
      >
        <DatePicker {...datePickerClearableProps} maxDate={dayjs()} />
      </Form.Item>

      {isTopAgentPlus ? (
        <Form.Item
          name="commissionsSettingsStatuses"
          label={t("commissions.settings.enums.commissionsSettingsStatus._label")}
          rules={[validations.none]}
          hidden={hidden}
        >
          <Select
            {...selectStandardProps}
            options={Object.values(CommissionsSettingsStatus).map((type) => ({
              value: type,
              label: t(`commissions.settings.enums.commissionsSettingsStatus.${type}`),
            }))}
            maxTagCount="responsive"
            mode="multiple"
            allowClear
          />
        </Form.Item>
      ) : undefined}

      <Form.Item
        name="statuses"
        label={isTopAgentPlus ? t("agent.helpers.agentStatusSFAPlusLabel") : t("agent.enums.agentStatus._label")}
        rules={[validations.none]}
        hidden={hidden}
      >
        <Select
          {...selectStandardProps}
          options={Object.values(AgentStatus).map((type) => ({
            value: type,
            label: t(`agent.enums.agentStatus.${type}`),
          }))}
          maxTagCount="responsive"
          mode="multiple"
          allowClear
        />
      </Form.Item>

      {isTopAgentPlus && (
        <>
          <Form.Item
            name="secondaryStatuses"
            label={t("agent.helpers.agentSecondaryStatusSFAPlusLabel")}
            rules={[validations.none]}
            hidden={hidden}
          >
            <Select
              {...selectStandardProps}
              options={Object.values(AgentSecondaryStatus).map((type) => ({
                value: type,
                label: t(`agent.enums.agentSecondaryStatus.${type}`),
              }))}
              maxTagCount="responsive"
              mode="multiple"
              allowClear
            />
          </Form.Item>

          <Form.Item
            name="completedEducationIds"
            label={t("agent.filter.completedEducationIds")}
            rules={[validations.none]}
            hidden={hidden}
          >
            <AgentEducationTreeSelect
              onChange={(value) => {
                if (value.length === 0) {
                  form.setFieldsValue({
                    completedEducationDateRange: undefined,
                    completedEducationDateMin: undefined,
                    completedEducationDateMax: undefined,
                  });
                }
              }}
            />
          </Form.Item>

          <Form.Item
            name="completedEducationDateRange"
            label={t("agent.filter.completedEducationDateRange")}
            {...rangePickerFormItemProps()}
            hidden={hidden}
          >
            <DatePicker.RangePicker
              style={{ width: "100%" }}
              format={getDatePickerFormat()}
              presets={getDatePickerDefaultRanges()}
              placeholder={[t("common.from"), t("common.to")]}
              onChange={(dates) => {
                if (!dates) return;

                const [from, to] = dates;

                form.setFieldsValue({
                  completedEducationDateMin: from ? dateToIsoDateString(from) : undefined,
                  completedEducationDateMax: to ? dateToIsoDateString(to) : undefined,
                });
              }}
              maxDate={dayjs()}
              disabled={!completedEducationIds?.length}
              allowEmpty={!completedEducationIds?.length}
            />
          </Form.Item>

          <HiddenInput name="completedEducationDateMin" />
          <HiddenInput name="completedEducationDateMax" />

          <Form.Item
            name="completedAcademies"
            label={t("agent.filter.completedAcademies")}
            rules={[validations.none]}
            hidden={hidden}
          >
            <Select<AgentEducationAcademyType[]>
              {...selectStandardProps}
              options={Object.values(AgentEducationAcademyType).map((type) => ({
                value: type,
                label: t(`agent.enums.agentEducationAcademyType.${type}`),
              }))}
              maxTagCount="responsive"
              mode="multiple"
              allowClear
              onChange={(value) => {
                if (value.length === 0) {
                  form.setFieldsValue({
                    completedAcademyDateRange: undefined,
                    completedAcademyDateMin: undefined,
                    completedAcademyDateMax: undefined,
                  });
                }
              }}
            />
          </Form.Item>

          <Form.Item
            name="completedAcademyDateRange"
            label={t("agent.filter.completedAcademyDateRange")}
            {...rangePickerFormItemProps()}
            hidden={hidden}
          >
            <DatePicker.RangePicker
              style={{ width: "100%" }}
              format={getDatePickerFormat()}
              presets={getDatePickerDefaultRanges()}
              placeholder={[t("common.from"), t("common.to")]}
              onChange={(dates) => {
                if (!dates) return;

                const [from, to] = dates;

                form.setFieldsValue({
                  completedAcademyDateMin: from ? dateToIsoDateString(from) : undefined,
                  completedAcademyDateMax: to ? dateToIsoDateString(to) : undefined,
                });
              }}
              maxDate={dayjs()}
              disabled={!completedAcademies?.length}
              allowEmpty={!completedAcademies?.length}
            />
          </Form.Item>

          <HiddenInput name="completedAcademyDateMin" />
          <HiddenInput name="completedAcademyDateMax" />
        </>
      )}

      <Form.Item
        name="onlyDirectSubordinates"
        valuePropName="checked"
        rules={[validations.none]}
        hidden={hidden}
        noStyle
      >
        <Checkbox>{t("agent.filter.onlyDirectSubordinates")}</Checkbox>
      </Form.Item>

      <Form.Item name="includeDeactivated" valuePropName="checked" rules={[validations.none]} hidden={hidden} noStyle>
        <Checkbox>{t("agent.filter.includeDeactivated")}</Checkbox>
      </Form.Item>

      <Form.Item
        name="includeRepresentatives"
        valuePropName="checked"
        rules={[validations.none]}
        hidden={hidden}
        noStyle
      >
        <Checkbox>{t("agent.filter.includeRepresentatives")}</Checkbox>
      </Form.Item>

      <Form.Item name="includeNonGainers" valuePropName="checked" rules={[validations.none]} hidden={hidden} noStyle>
        <Checkbox>{t("agent.filter.includeNonGainers")}</Checkbox>
      </Form.Item>

      <Form.Item
        name="onlyRealtySpecialists"
        valuePropName="checked"
        rules={[validations.none]}
        hidden={hidden}
        noStyle
      >
        <Checkbox>{t("agent.filter.onlyRealtySpecialists")}</Checkbox>
      </Form.Item>

      <Form.Item name="onlyLoanSpecialists" valuePropName="checked" rules={[validations.none]} hidden={hidden} noStyle>
        <Checkbox>{t("agent.filter.onlyLoanSpecialists")}</Checkbox>
      </Form.Item>

      {includeZeroBalance ? (
        <Form.Item name="includeZeroBalance" valuePropName="checked" rules={[validations.none]} hidden={hidden} noStyle>
          <Checkbox>{t("commissions.debtAccount.filter.includeZeroBalance")}</Checkbox>
        </Form.Item>
      ) : undefined}
    </>
  );
};
